import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { uuid } from "@ant-design/plots/es/utils";
import { Badge, Button, Layout, Menu, Popover, Tag } from "antd";
import {
  BriefcaseIcon,
  DashboardIcon,
  FileNumberIcon,
  MessageIcon,
  PieChartIcon,
  UsersIcon,
  WalletIcon,
} from "assets/icons";
import { COLORS, USER_TYPE, WORKSPACE_USER_TYPE } from "constants/appConstant";
import SwitchWorkspace from "pages/admin/switchWorkspace";
import { changeLayout, setIsRefresh } from "stores/slices/layout";

import logo from "../assets/images/2d-logo-transparent.png";
import iconLogo from "../assets/images/icon-white-transparent.png";
import { AuthInfo } from "../stores/slices/auth";
import {
  doGetWorkspaceEvent,
  doGetWorkspacePage,
  getRealtimeWorkspace,
  getWorkspaceInfo,
  WorkspaceInfo,
} from "../stores/slices/workspace";

import styles from "./styles.module.scss";

const Sidebar = props => {
  const { setBroken, collapsed, setCollapsed, currentMenu, setCurrentMenu } =
    props;

  const { t } = useTranslation();

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const authInfo = useSelector(AuthInfo);
  const workspaceRealtime = useSelector(getRealtimeWorkspace);
  const workspaceInfo = useSelector(WorkspaceInfo);
  const [workspaceListItem, setWorkspaceListItem] = useState([]);

  const [isOpenSwitchWorkspace, setIsOpenSwitchWorkspace] = useState(false);
  const [numberReviewMessage, setNumberReviewMessage] = useState(0);
  const handleChangeMenu = e => {
    switch (e.key) {
      case "dashboard":
        setCurrentMenu(e.key);
        dispatch(setIsRefresh(uuid()));
        navigate("/admin/dashboard");
        break;
      case "workspaces":
        setCurrentMenu(e.key);
        navigate("/admin/workspaces");
        dispatch(setIsRefresh(uuid()));
        break;
      case "users":
        setCurrentMenu(e.key);
        navigate(
          `/admin/users/workspace/${
            authInfo.type === USER_TYPE.SUPER_ADMIN ? "all" : "default"
          }`
        );
        dispatch(setIsRefresh(uuid()));
        break;
      case "users-online-now":
        setCurrentMenu(e.key);
        navigate(
          `/admin/users-online-now/workspace/${
            authInfo.type === USER_TYPE.SUPER_ADMIN
              ? "all"
              : authInfo.workspaceId
          }`
        );
        dispatch(setIsRefresh(uuid()));
        break;
      case "past":
        setCurrentMenu(e.key);
        navigate("/admin/past");
        dispatch(setIsRefresh(uuid()));
        break;
      case "future":
        setCurrentMenu(e.key);
        navigate("/admin/future");
        dispatch(setIsRefresh(uuid()));
        break;
      case "review":
        setCurrentMenu(e.key);
        navigate("/admin/review");
        dispatch(setIsRefresh(uuid()));
        break;
      case "templates":
        setCurrentMenu(e.key);
        navigate(
          `/admin/templates/${
            authInfo.type === USER_TYPE.SUPER_ADMIN ? "all" : "default"
          }`
        );
        dispatch(setIsRefresh(uuid()));
        break;
      case "broadcast-list":
        setCurrentMenu(e.key);
        navigate("/admin/broadcast-list");
        dispatch(setIsRefresh(uuid()));
        break;
      case "custom-field":
        setCurrentMenu(e.key);
        navigate("/admin/custom-field");
        dispatch(setIsRefresh(uuid()));
        break;
      case "customer":
        setCurrentMenu(e.key);
        navigate("/admin/customer/all-customer/all");
        dispatch(setIsRefresh(uuid()));
        break;
      case "invoices":
        setCurrentMenu(e.key);
        navigate(
          `/admin/invoices/${
            authInfo.type === USER_TYPE.SUPER_ADMIN ? "all" : "default"
          }`
        );
        dispatch(setIsRefresh(uuid()));
        break;
      case "services":
        setCurrentMenu(e.key);
        navigate(
          `/admin/services/${
            authInfo.type === USER_TYPE.SUPER_ADMIN ? "all" : "default"
          }`
        );
        dispatch(setIsRefresh(uuid()));
        break;
      case "channels":
        setCurrentMenu(e.key);
        navigate("/admin/channels");
        dispatch(setIsRefresh(uuid()));
        break;
      case "call-history":
        setCurrentMenu(e.key);
        navigate("/admin/call-history");
        dispatch(setIsRefresh(uuid()));
        break;
      default:
        break;
    }
  };

  const getItem = ({ label, key, icon, children, disabled }) => {
    return {
      key,
      icon,
      children,
      label,
      disabled,
    };
  };

  const items = [
    getItem({
      label: t("title.dashboard"),
      key: "dashboard",
      icon: <DashboardIcon />,
    }),
    getItem({
      label: t("title.workspaces"),
      key: "workspaces",
      icon: <BriefcaseIcon />,
      onClick: () => {
        console.log("clicked workspace");
      },
    }),
    getItem({
      label: t("title.channels"),
      key: "channels",
      icon: <BriefcaseIcon />,
      disabled:
        authInfo.type === USER_TYPE.USER &&
        (!authInfo.workspaceRole ||
          authInfo.workspaceRole !== WORKSPACE_USER_TYPE.ADMIN),
    }),
    getItem({
      label: t("title.services"),
      key: "services",
      icon: <FileNumberIcon />,
      disabled:
        authInfo.type === USER_TYPE.USER &&
        (!authInfo.workspaceRole ||
          authInfo.workspaceRole !== WORKSPACE_USER_TYPE.ADMIN),
    }),
    getItem({
      label: t("title.userManagement"),
      key: "sub0",
      icon: <PieChartIcon />,
      children: [
        getItem({
          label: t("title.users"),
          key: "users",
          disabled:
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
        getItem({
          label: t("title.usersOnlineNow"),
          key: "users-online-now",
          disabled:
            authInfo.type === USER_TYPE.USER &&
            (!authInfo.workspaceRole ||
              authInfo.workspaceRole === USER_TYPE.USER ||
              authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR),
        }),
      ],
    }),
    getItem({
      label: t("title.customers"),
      key: "sub3",
      icon: <UsersIcon />,
      children: [
        getItem({
          label: t("title.broadcastLists"),
          key: "broadcast-list",
          disabled:
            // authInfo.type === USER_TYPE.SUPER_ADMIN ||
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
        getItem({
          label: t("title.customFields"),
          key: "custom-field",
          disabled:
            // authInfo.type === USER_TYPE.SUPER_ADMIN ||
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
        getItem({
          label: t("title.allCustomers"),
          key: "customer",
          disabled:
            // authInfo.type === USER_TYPE.SUPER_ADMIN ||
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
      ],
    }),
    getItem({
      label: <span className="sidebar-message">{t("title.messages")}</span>,
      key: "sub2",
      icon: <MessageIcon />,
      children: [
        getItem({
          label: t("title.pastBroadcast"),
          key: "past",
          disabled:
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
        getItem({
          label: t("title.futureBroadcast"),
          key: "future",
          disabled:
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
        getItem({
          label: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <span>{t("title.review")}</span>
              {authInfo.type !== USER_TYPE.SUPER_ADMIN &&
                numberReviewMessage > 0 && (
                  <Badge
                    count={numberReviewMessage}
                    color="#F36D44"
                    className="sidebar-badge"
                  />
                )}
            </div>
          ),
          key: "review",
          disabled:
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
        getItem({
          label: t("title.templates"),
          key: "templates",
          disabled:
            (authInfo.type === USER_TYPE.USER && !authInfo.workspaceRole) ||
            authInfo.workspaceRole === WORKSPACE_USER_TYPE.OPERATOR,
        }),
      ],
    }),
    // getItem({
    //   label: t("title.callHistory"),
    //   key: "call-history",
    //   icon: <PhoneIcon color="#ffffff" width={19} height={19} />,
    //   disabled:
    //     authInfo.type === USER_TYPE.USER &&
    //     (!authInfo.workspaceRole ||
    //       authInfo.workspaceRole !== WORKSPACE_USER_TYPE.ADMIN),
    // }),
    getItem({
      label: t("title.invoices"),
      key: "invoices",
      icon: <WalletIcon />,
      disabled:
        authInfo.type === USER_TYPE.USER &&
        (!authInfo.workspaceRole ||
          authInfo.workspaceRole !== WORKSPACE_USER_TYPE.ADMIN),
    }),
  ];

  const handleChangeLayout = () => {
    dispatch(changeLayout("OPERATOR"));
  };

  const doGetWorkspace = async () => {
    const params = {
      pageNumber: 1,
      pageSize: 10,
      sortFieldWorkspace: "createdTime",
      sortDirection: "DESC",
    };
    const { payload } = await dispatch(doGetWorkspacePage(params));
    if (payload && payload.content && payload.content.length > 0) {
      setWorkspaceListItem(payload.content);
    }
  };

  const handleOpenChange = newOpen => {
    setIsOpenSwitchWorkspace(newOpen);
  };

  useEffect(() => {
    doGetWorkspace();
  }, []);

  useEffect(() => {
    if (authInfo && authInfo.workspaceId) {
      // console.log("authInfo.workspaceId", authInfo.workspaceId);
      dispatch(doGetWorkspaceEvent({ workspaceId: authInfo.workspaceId }));
      dispatch(getWorkspaceInfo(authInfo.workspaceId));
    }
  }, [authInfo?.workspaceId]);

  useEffect(() => {
    if (workspaceRealtime?.properties?.numberReviewMessage !== undefined) {
      setNumberReviewMessage(
        workspaceRealtime?.properties?.numberReviewMessage
      );
    } else {
      setNumberReviewMessage(workspaceInfo.numberReviewMessage);
    }
  }, [
    workspaceRealtime?.properties?.numberReviewMessage,
    workspaceInfo.numberReviewMessage,
  ]);

  return (
    <React.Fragment>
      <Layout.Sider
        width={250}
        collapsedWidth={50}
        breakpoint="lg"
        collapsed={collapsed}
        onBreakpoint={broken => {
          setBroken(broken);
        }}
        onCollapse={collapsed => {
          setCollapsed(collapsed);
        }}
        style={{
          background: COLORS.MAIN_COLOR,
          position: "fixed",
          zIndex: 2,
        }}
        className={styles.antLayout}
      >
        <div>
          <div className={styles.sidebarLogo}>
            <img
              src={iconLogo}
              alt="logo"
              width={50}
              height={50}
              style={{ width: collapsed ? "100%" : 50, height: "auto" }}
            />
            {!collapsed && (
              <>
                {authInfo && authInfo.workspaceName && (
                  <>
                    {workspaceListItem && workspaceListItem.length > 1 ? (
                      <>
                        <Popover
                          content={
                            <>
                              <SwitchWorkspace
                                setIsOpenSwitchWorkspace={
                                  setIsOpenSwitchWorkspace
                                }
                              />
                            </>
                          }
                          trigger="click"
                          open={isOpenSwitchWorkspace}
                          onOpenChange={handleOpenChange}
                          arrow={false}
                          placement="topLeft"
                        >
                          <Button
                            color={"primary"}
                            type="primary"
                            htmlType="button"
                            style={{ overflow: "hidden", maxWidth: 230 }}
                          >
                            <span
                              style={{
                                display: "block",
                                textOverflow: "ellipsis",
                                overflow: "hidden",
                                width: "100%",
                              }}
                              title={
                                workspaceRealtime &&
                                workspaceRealtime.properties &&
                                workspaceRealtime.properties.name !== undefined
                                  ? workspaceRealtime.properties.name
                                  : authInfo.workspaceName
                              }
                            >
                              {workspaceRealtime &&
                              workspaceRealtime.properties &&
                              workspaceRealtime.properties.name !== undefined
                                ? workspaceRealtime.properties.name
                                : authInfo.workspaceName}
                            </span>
                          </Button>
                        </Popover>
                      </>
                    ) : (
                      <>
                        <Tag
                          color="#1677ff"
                          className={styles.sidebarWorkspace}
                          title={authInfo.workspaceName}
                        >
                          {authInfo.workspaceName}
                        </Tag>
                      </>
                    )}
                  </>
                )}
                {authInfo &&
                  !authInfo.workspaceName &&
                  authInfo.type === USER_TYPE.SUPER_ADMIN && (
                    <Tag
                      color="#1677ff"
                      className={styles.sidebarWorkspace}
                      title={t("role.superAdmin")}
                    >
                      {t("role.superAdmin")}
                    </Tag>
                  )}
              </>
            )}
          </div>
          <Menu
            defaultSelectedKeys={["dashboard"]}
            selectedKeys={[currentMenu]}
            onClick={handleChangeMenu}
            mode="inline"
            items={items}
            forceSubMenuRender={true}
            className={`${styles.sidebarMenu} sidebar-menu`}
            // className={"sidebarMenu"}
            style={{
              padding: collapsed ? 0 : "0 8px",
            }}
          />
        </div>
        <div className={styles.sidebarLogoBottom}>
          {authInfo.type !== USER_TYPE.SUPER_ADMIN && (
            <Button
              color={"primary"}
              icon={<UsersIcon />}
              type="primary"
              onClick={handleChangeLayout}
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginBottom: "10px",
              }}
            >
              {collapsed ? "" : t("button.operatorConsole")}
            </Button>
          )}
          <img
            src={logo}
            alt="logo"
            width={50}
            height={50}
            style={{ width: "100%", height: "auto" }}
          />
        </div>
      </Layout.Sider>
    </React.Fragment>
  );
};

export default Sidebar;
